<script setup lang="ts">
import { computed, PropType, ref, onMounted, onUnmounted } from "vue";
import { isActive as isOfficeActive } from "../store/Office";
import isSchemaObject from "../lib/isSchemaObject";
import SchemaType from "../model/schemaType";
import isSchemaArray from "../lib/isSchemaArray";
import Schema from "../model/schema";
import Labels from "../model/labels";
import HelpTooltip from "./HelpTooltip.vue";
import { showToast } from "../store/Toast";

const props = defineProps({
  marker: {
    type: Object as PropType<Schema>,
    required: true,
  },
  slug: {
    type: String,
    required: true,
  },
});

const showOptionalMarkers = ref(false);

// Écouter l'événement d'expansion pour ouvrir les marqueurs supplémentaires si nécessaire
const handleExpandEvent = (event: CustomEvent) => {
  const targetSlug = event.detail.slug;
  // Vérifier si le slug cible est un enfant de cette section
  if (targetSlug && targetSlug.startsWith(props.slug + '.')) {
    showOptionalMarkers.value = true;
  }
};

onMounted(() => {
  window.addEventListener('expand-optional-markers', handleExpandEvent as EventListener);
});

onUnmounted(() => {
  window.removeEventListener('expand-optional-markers', handleExpandEvent as EventListener);
});

// propriétés "simples" obligatoires
const mandatoryMarkersList = computed(() => {
  if (isSchemaObject(props.marker)) {
    const properties = { ...props.marker.properties };

    if (props.marker.required != undefined) {
      let markersRequiredKeys = Object.values(props.marker.required);
      for (const [key] of Object.entries(properties)) {
        if (
          !markersRequiredKeys.includes(key) ||
          undefined === properties[key].description
        ) {
          delete properties[key];
        }
      }
    }

    return properties;
  }
});

// "sous-schémas" obligatoires
const mandatorySubMarkersList = computed(() => {
  if (isSchemaObject(props.marker)) {
    const properties = { ...props.marker.properties };

    if (props.marker.required != undefined) {
      let markersRequiredKeys = Object.values(props.marker.required);
      for (const [key] of Object.entries(properties)) {
        if (
          !markersRequiredKeys.includes(key) ||
          undefined !== properties[key].description
        ) {
          delete properties[key];
        }
      }
      return properties;
    }
  }
});

const optionalMarkersList = computed(() => {
  if (isSchemaObject(props.marker)) {
    const properties = { ...props.marker.properties };

    if (props.marker.required != undefined) {
      let markersRequiredKeys = Object.values(props.marker.required);
      for (const [key] of Object.entries(properties)) {
        if (markersRequiredKeys.includes(key)) {
          delete properties[key];
        }
      }
    }
    return properties;
  }
});

const setAnchor = computed(() => {
  return props.slug.split("[i]").join("").split(".").slice(1).join(" > ");
});

const iconOptionalMarkersClass = ref("");

const switchShowOptional = () => {
  showOptionalMarkers.value = !showOptionalMarkers.value;
  iconOptionalMarkersClass.value = showOptionalMarkers.value
    ? "-rotate-180"
    : "";
};


// Compte le nombre de marqueurs finaux dans une liste
const countFinalMarkers = (markersList: any): number => {
  if (!markersList) return 0;
  let count = 0;
  for (const key in markersList) {
    if (markersList[key].description) {
      count++;
    }
  }
  return count;
};

const markerHasOptional = (marker: Schema) => {
  let result = false;
  if (isSchemaObject(marker) && marker.required) {
    Object.keys(marker.properties).forEach(function (key: string) {
      if (!marker.required!.includes(key)) {
        result = true;
      }
    });
  }
  return result;
};

const headersLabel = (input: string) => {
  let labelsArray: string[] = input.split("[i]").join("").split(".").slice(1);
  let i = 0;
  labelsArray.forEach(function (elt) {
    labelsArray[i] = Object.keys(Labels).includes(elt) ? Labels[elt] : elt;
    i++;
  });
  return labelsArray.join(" > ");
};

const copy = (str: string) => {
  const el = document.createElement("textarea");
  el.value = str;
  el.setAttribute("readonly", "");
  el.style.position = "absolute";
  el.style.left = "-9999px";
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);

  // Afficher la notification
  showToast("Marqueur copié !", "success");
};

const insert = async (str: string) => {
  try {
    if (typeof Word !== 'undefined') {
      await Word.run((context) => {
        context.document
          .getSelection()
          .insertText(str, Word.InsertLocation.replace);
        return context.sync();
      });

      // Afficher la notification
      showToast("Marqueur inséré !", "success");
    } else {
      copy(str);
    }
  } catch (error) {
    console.error("Erreur lors de l'insertion:", error);
    copy(str);
  }
};

// Fonction pour copier tout le tableau
const copyTable = async () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    showToast(
      "Fonction disponible uniquement dans votre navigateur.",
      "error"
    );
    return;
  }
  if (
    !isSchemaArray(props.marker) ||
    !props.marker.items ||
    !isSchemaObject(props.marker.items)
  ) {
    showToast("Ce tableau ne contient pas de colonnes exploitables.", "info");
    return;
  }

  const ClipboardItemConstructor = (window as any).ClipboardItem;
  if (!navigator.clipboard || typeof ClipboardItemConstructor === "undefined") {
    showToast(
      "Copie avancée non disponible dans ce navigateur. Essayez un copier/coller manuel.",
      "error"
    );
    return;
  }

  const items = props.marker.items.properties;
  const table = document.createElement("table");
  const thead = document.createElement("thead");
  const tbody = document.createElement("tbody");
  const trHead = document.createElement("tr");
  const trBody1 = document.createElement("tr");
  const trBody2 = document.createElement("tr");

  table.style.width = "100%";
  table.style.borderCollapse = "collapse";
  table.style.fontFamily = "Arial, sans-serif";
  table.style.fontSize = "10pt";

  for (const key in items) {
    if (items[key].description) {
      const th = document.createElement("th");
      th.innerText = items[key].description;
      th.style.backgroundColor = "#D3D3D3";
      th.style.color = "white";
      th.style.border = "1px solid black";
      th.style.padding = "8px";
      th.style.textAlign = "left";
      trHead.appendChild(th);

      // Première ligne avec [i]
      const td1 = document.createElement("td");
      let markerCode1 = `{${props.slug}[i].${key}}`;
      if (items[key].type === SchemaType.Date)
        markerCode1 = `{${props.slug}[i].${key}:formatD(DD/MM/YYYY)}`;
      else if (items[key].type === SchemaType.DateTime)
        markerCode1 = `{${props.slug}[i].${key}:formatD('L [à] HH[h]mm')}`;
      else if (items[key].type === SchemaType.Currency)
        markerCode1 = `{${props.slug}[i].${key}:formatC()}`;
      else if (items[key].type === SchemaType.Text)
        markerCode1 = `{${props.slug}[i].${key}:convCRLF()}`;
      td1.innerText = markerCode1;
      td1.style.border = "1px solid black";
      td1.style.padding = "8px";
      td1.style.textAlign = "left";
      trBody1.appendChild(td1);

      // Deuxième ligne avec [i+1]
      const td2 = document.createElement("td");
      let markerCode2 = `{${props.slug}[i+1].${key}}`;
      if (items[key].type === SchemaType.Date)
        markerCode2 = `{${props.slug}[i+1].${key}:formatD(DD/MM/YYYY)}`;
      else if (items[key].type === SchemaType.DateTime)
        markerCode2 = `{${props.slug}[i+1].${key}:formatD('L [à] HH[h]mm')}`;
      else if (items[key].type === SchemaType.Currency)
        markerCode2 = `{${props.slug}[i+1].${key}:formatC()}`;
      else if (items[key].type === SchemaType.Text)
        markerCode2 = `{${props.slug}[i+1].${key}:convCRLF()}`;
      td2.innerText = markerCode2;
      td2.style.border = "1px solid black";
      td2.style.padding = "8px";
      td2.style.textAlign = "left";
      trBody2.appendChild(td2);
    }
  }

  thead.appendChild(trHead);
  tbody.appendChild(trBody1);
  tbody.appendChild(trBody2);
  table.appendChild(thead);
  table.appendChild(tbody);

  try {
    const tableHTML = table.outerHTML;
    const blob = new Blob([tableHTML], { type: "text/html" });
    const data = [new ClipboardItemConstructor({ "text/html": blob })];
    await navigator.clipboard.write(data);
    showToast("Tableau complet copié !", "success");
  } catch (err) {
    console.error("Erreur copie tableau:", err);
    showToast("Erreur lors de la copie du tableau", "error");
  }
};

const copyOrInsert = async (str: string) => {
  if (isOfficeActive) {
    await insert(str);
  } else {
    copy(str);
  }
};

const balise = computed(() => {
  switch (props.marker.type) {
    case SchemaType.Date:
      return "{" + props.slug + ":formatD(DD/MM/YYYY)}";
    case SchemaType.Time:
      return "{" + props.slug + ":formatD(H[h]mm,hh:mm:ss)}";
    case SchemaType.DateTime:
      return "{" + props.slug + ":formatD('L [à] HH[h]mm')}";
    case SchemaType.Currency:
      return "{" + props.slug + ":formatC()}";
    case SchemaType.Image:
      return "{" + props.slug + ":image(5cm)}";
    case SchemaType.Text:
      return "{" + props.slug + ":convCRLF()}";
    default:
      return "{" + props.slug + "}";
  }
});

// Explications des formats
const getFormatExplanation = computed(() => {
  switch (props.marker.type) {
    case SchemaType.Date:
      return {
        title: "Format de date",
        content: "La date sera formatée selon le modèle DD/MM/YYYY. Vous pouvez personnaliser le format dans le marqueur."
      };
    case SchemaType.DateTime:
      return {
        title: "Format date et heure",
        content: "Date et heure au format français. Le 'L' représente la date complète et HH[h]mm l'heure."
      };
    case SchemaType.Currency:
      return {
        title: "Format monétaire",
        content: "Formate le nombre en devise avec le symbole € et les décimales appropriées."
      };
    case SchemaType.Text:
      return {
        title: "Conversion retours chariot",
        content: "ConvCRLF() convertit les retours à la ligne pour un affichage correct dans Word."
      };
    case SchemaType.Image:
      return {
        title: "Format image",
        content: "Insère une image avec la taille spécifiée (5cm par défaut). Modifiable selon vos besoins."
      };
    default:
      return null;
  }
});
</script>

<template>
  <div v-if="isSchemaObject(marker) || isSchemaArray(marker)" class="mt-4">
    <!-- En-têtes hiérarchisés épurés avec fil d'Ariane -->
    <div class="flex flex-col xl:mt-4 items-start content-start">
      <span v-if="slug.endsWith('[i]')" class="hidden"></span>

      <!-- Niveau 2 : Section principale avec design subtil -->
      <div
        v-else-if="slug.split('.').length === 2"
        class="w-full"
        :data-anchor-slug="slug"
        data-anchor-level="2"
      >
        <div class="
            flex
            items-baseline
            justify-between
            mb-4
            mt-6
            pb-3
            border-b-2
            border-gray-200
            dark:border-gray-700
            gap-3
            min-w-0
          ">
          <h2
            class="
              font-semibold
              text-xl
              capitalize
              text-gray-900
              dark:text-gray-100
              flex
              items-center
              gap-2
              min-w-0
              flex-1
            "
            :id="setAnchor"
          >
            <span class="w-1 h-6 bg-primary-500 dark:bg-primary-400 rounded-full flex-shrink-0"></span>
            <span class="truncate">{{ headersLabel(slug) }}</span>
          </h2>
          <!-- Badge indicateur de marqueurs -->
          <span v-if="countFinalMarkers(mandatoryMarkersList) > 0"
                class="
                  text-xs
                  px-2.5
                  py-1
                  rounded-full
                  bg-primary-100
                  dark:bg-primary-900/30
                  text-primary-700
                  dark:text-primary-400
                  font-medium
                  flex-shrink-0
                ">
            {{ countFinalMarkers(mandatoryMarkersList) }} marqueur{{ countFinalMarkers(mandatoryMarkersList) > 1 ? 's' : '' }}
          </span>
        </div>
      </div>

      <!-- Niveau 3 : Sous-section avec fil d'Ariane et bordure -->
      <div
        v-else-if="slug.split('.').length === 3"
        class="w-full mt-4"
        :data-anchor-slug="slug"
        data-anchor-level="3"
      >
        <div class="
            ml-4
            pl-4
            border-l-2
            border-gray-300
            dark:border-gray-600
          ">
          <!-- Fil d'Ariane -->
          <div class="text-xs text-gray-500 dark:text-gray-500 mb-2 flex items-center gap-1 min-w-0 overflow-hidden">
            <template v-for="(part, index) in headersLabel(slug).split(' > ')" :key="index">
              <span v-if="index > 0" class="text-gray-400 dark:text-gray-600 flex-shrink-0">›</span>
              <span class="truncate" :class="index === headersLabel(slug).split(' > ').length - 1 ? 'text-gray-700 dark:text-gray-300 font-medium' : ''">
                {{ part }}
              </span>
            </template>
          </div>
          <h3
            class="
              mb-3
              font-medium
              capitalize
              text-gray-900
              dark:text-gray-100
              truncate
            "
          >
            {{ headersLabel(slug).split(' > ').pop() }}
          </h3>
        </div>
      </div>

      <!-- Niveau 4 : Détails avec fil d'Ariane et indentation -->
      <div
        v-else-if="slug.split('.').length === 4"
        class="w-full mt-3"
        :data-anchor-slug="slug"
        data-anchor-level="4"
      >
        <div class="ml-8">
          <!-- Fil d'Ariane -->
          <div class="text-xs text-gray-500 dark:text-gray-500 mb-1.5 flex items-center gap-1 min-w-0 overflow-hidden">
            <template v-for="(part, index) in headersLabel(slug).split(' > ')" :key="index">
              <span v-if="index > 0" class="text-gray-400 dark:text-gray-600 flex-shrink-0">›</span>
              <span class="truncate" :class="index === headersLabel(slug).split(' > ').length - 1 ? 'text-gray-700 dark:text-gray-300 font-medium' : ''">
                {{ part }}
              </span>
            </template>
          </div>
          <h4
            class="
              mb-2
              capitalize
              text-gray-800
              dark:text-gray-200
              font-medium
              text-sm
              truncate
            "
          >
            {{ headersLabel(slug).split(' > ').pop() }}
          </h4>
        </div>
      </div>
    </div>

    <!-- Info détaillée pour les tableaux (affichée pour TOUTES les listes, quel que soit le niveau) -->
    <div v-if="isSchemaArray(marker) && !slug.endsWith('[i]')" class="
        mb-4
        p-4
        bg-blue-50
        dark:bg-blue-900/10
        border-l-4
        border-blue-500
        dark:border-blue-400
        rounded-r-lg
        text-sm
      "
      :class="{
        'ml-4': slug.split('.').length === 3,
        'ml-8': slug.split('.').length === 4,
        'ml-12': slug.split('.').length >= 5
      }"
    >
      <div class="flex items-start gap-3">
        <fa :icon="['far', 'info-circle']" class="text-blue-600 dark:text-blue-400 mt-0.5 text-lg flex-shrink-0" />
        <div class="flex-1">
          <p class="text-blue-900 dark:text-blue-100 font-semibold mb-2">
            📋 Marqueurs de liste (tableau répétable)
          </p>
          <p class="text-blue-800 dark:text-blue-200 text-sm mb-3">
            Les marqueurs contenant <code class="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 rounded font-semibold">[i]</code>
            représentent des <strong>données répétables</strong>.
          </p>

          <!-- Exemple visuel -->
          <div class="bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-700 rounded p-3 mb-2">
            <p class="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">💡 Comment utiliser :</p>
            <ol class="text-xs text-gray-700 dark:text-gray-300 space-y-1.5 list-decimal list-inside">
              <li>Créez un <strong>tableau dans Word</strong></li>
              <li>Insérez le marqueur dans une cellule (ex: <code class="text-xs px-1 bg-gray-100 dark:bg-gray-700 rounded">{{"{" + slug + "[i].propriete}"}}</code>)</li>
              <li><strong>Répétez le marqueur</strong> sur la ligne suivante en incrémentant : <code class="text-xs px-1 bg-gray-100 dark:bg-gray-700 rounded">[i+1]</code></li>
              <li>Le système répétera automatiquement les lignes pour chaque élément</li>
            </ol>
          </div>

          <p class="text-xs text-blue-700 dark:text-blue-300 italic">
            Exemple : Une liste de 5 éléments créera automatiquement 5 lignes dans votre tableau Word.
          </p>

          <!-- Bouton Copier le tableau -->
          <div class="mt-3">
            <button
              @click="copyTable"
              class="
                inline-flex
                items-center
                gap-2
                px-3
                py-1.5
                text-xs
                font-medium
                text-white
                bg-blue-600
                hover:bg-blue-700
                rounded
                transition-colors
                duration-150
                shadow-sm
              "
            >
              <fa :icon="['far', 'copy']" />
              Copier le tableau modèle
            </button>
          </div>
        </div>
      </div>
    </div>

    <ul v-if="mandatoryMarkersList">
      <ListMarkers
        v-for="(child, key) in mandatoryMarkersList"
        :key="key"
        :slug="slug + '.' + key"
        :marker="child"
      />
    </ul>

    <!-- Section Marqueurs Optionnels avec UI claire -->
    <div v-if="markerHasOptional(marker)" class="mt-4 mb-4 clear-both">
      <!-- Séparateur visuel -->
      <div class="flex items-center gap-3 mb-3">
        <div class="flex-1 h-px bg-gray-300 dark:bg-gray-700"></div>
            <span class="text-xs text-gray-500 dark:text-gray-500 font-medium uppercase tracking-wide">
          Marqueurs supplémentaires
        </span>
        <div class="flex-1 h-px bg-gray-300 dark:bg-gray-700"></div>
      </div>

      <!-- Bouton toggle avec preview -->
      <button
        @click="switchShowOptional"
        class="
          w-full
          flex
          items-center
          gap-2
          px-4
          py-3
          text-sm
          bg-gray-50
          dark:bg-gray-800/50
          border
          border-gray-200
          dark:border-gray-700
          text-gray-700
          dark:text-gray-300
          font-medium
          rounded-lg
          hover:border-primary-500
          dark:hover:border-primary-500
          hover:bg-white
          dark:hover:bg-gray-800
          transition-all
          duration-150
          cursor-pointer
        "
      >
        <div class="flex items-center gap-2">
          <fa
            :icon="['fas', 'angle-down']"
            class="transition-transform duration-200 text-base"
            :class="iconOptionalMarkersClass"
          />
          <span v-if="showOptionalMarkers">
            Masquer les marqueurs supplémentaires
          </span>
          <span v-else class="flex items-center gap-2">
            Afficher {{ Object.keys(optionalMarkersList || {}).length }} marqueurs supplémentaires
            <span class="
                px-2
                py-0.5
                text-xs
                bg-primary-100
                dark:bg-primary-900/30
                text-primary-700
                dark:text-primary-400
                rounded-full
              ">
              +{{ Object.keys(optionalMarkersList || {}).length }}
            </span>
          </span>
        </div>

      </button>
    </div>

    <!-- Marqueurs supplémentaires (avec animation) -->
    <Transition name="expand">
      <ul v-if="optionalMarkersList && showOptionalMarkers" class="mt-2">
        <ListMarkers
          v-for="(child, key) in optionalMarkersList"
          :key="key"
          :slug="slug + '.' + key"
          :marker="child"
        />
      </ul>
    </Transition>

    <!-- Sous-marqueurs obligatoires -->
    <ul v-if="mandatorySubMarkersList">
      <ListMarkers
        v-for="(child, key) in mandatorySubMarkersList"
        :key="key"
        :slug="slug + '.' + key"
        :marker="child"
      />
    </ul>

    <!-- Tableaux -->
    <ul v-else-if="isSchemaArray(marker)">
      <ListMarkers :key="slug" :slug="slug + '[i]'" :marker="marker.items" />
    </ul>
  </div>
  <!-- Élément de marqueur épuré et lisible - N'affiche QUE les valeurs finales -->
  <li
    v-else-if="marker.description"
      class="
      grid
      md:grid-cols-[1fr_auto]
      gap-3
      py-3
      border-b
      border-gray-200
      dark:border-gray-700
      hover:bg-gray-50
      dark:hover:bg-gray-800/30
      transition-colors
      duration-150
      min-w-0
    "
    :class="{
      'ml-4': slug.split('.').length === 3,
      'ml-8': slug.split('.').length === 4,
      'ml-12': slug.split('.').length >= 5
    }"
    :data-anchor-slug="slug"
    data-anchor-level="item"
  >
    <!-- Description du marqueur avec aide -->
    <div class="flex items-center gap-1 min-w-0 flex-1">
      <p class="text-gray-700 dark:text-gray-300 truncate">
        {{ marker.description }}
      </p>
      <!-- Tooltip d'aide pour les formats -->
      <HelpTooltip
        v-if="getFormatExplanation"
        :title="getFormatExplanation.title"
        :content="getFormatExplanation.content"
      />
    </div>

    <!-- Code du marqueur - Lisible et fonctionnel -->
    <code
      class="
        group
        inline-flex
        items-center
        gap-2
        px-3
        py-1.5
        text-sm
        font-mono
        bg-gray-100
        dark:bg-gray-800
        text-primary-700
        dark:text-primary-300
        border
        border-gray-300
        dark:border-gray-600
        rounded
        hover:bg-primary-50
        dark:hover:bg-primary-900/20
        hover:border-primary-500
        dark:hover:border-primary-500
        cursor-pointer
        transition-all
        duration-150
        max-w-full
        min-w-0
      "
      @click="copyOrInsert(balise)"
      :title="isOfficeActive ? 'Cliquer pour insérer' : 'Cliquer pour copier'"
    >
      <!-- Code du marqueur visible -->
      <span class="font-semibold truncate">
        {{ balise }}
      </span>

      <!-- Icône d'action -->
      <fa
        :icon="isOfficeActive ? ['far', 'plus-square'] : ['far', 'copy']"
        class="text-gray-500 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 flex-shrink-0"
      />
    </code>
  </li>
</template>

<style scoped>
/* Animation pour l'expansion des marqueurs supplémentaires */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.expand-enter-to,
.expand-leave-from {
  opacity: 1;
  max-height: 2000px;
}
</style>
